import { OptionNameValuePair } from "@/src/lib/interfaces/types";
import { Plus, Trash2 } from "lucide-react";
import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { cn } from "../../../lib/utils";
import DateCell from "./cells/DateCell";
import MultiselectCell from "./cells/MultiselectCell";
import NumberCell from "./cells/NumberCell";
import SelectCell from "./cells/SelectCell";
import TextButtonCell from "./cells/TextButtonCell";
import TextCell from "./cells/TextCell";
import {
  ACTIVE_CELL_BG,
  ACTIVE_CELL_BORDER,
  CHECKBOX_BASE_CLASSES,
} from "./constants";

const RowWidgetsPortal = ({
  anchorEl,
  children,
  className,
  style,
}: {
  anchorEl: HTMLTableCellElement | null;
  children: ReactNode;
  className: string;
  style: CSSProperties;
}) => {
  const [portalStyle, setPortalStyle] = useState<CSSProperties | null>(null);

  const findHorizontalScrollParent = (el: Element | null): HTMLElement | null => {
    let node = el?.parentElement as HTMLElement | null;
    while (node) {
      if (node === document.body) return null;
      const s = getComputedStyle(node);
      const ox = s.overflowX;
      const o = s.overflow;
      if (ox === "auto" || ox === "scroll" || o === "auto" || o === "scroll") {
        return node;
      }
      node = node.parentElement as HTMLElement | null;
    }
    return null;
  };

  const parsePx = (v: unknown) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const updatePosition = () => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const scrollParent = findHorizontalScrollParent(anchorEl);
    const scrollRect = scrollParent?.getBoundingClientRect();
    const marginLeftPx = parsePx(style.marginLeft);

    let left = rect.left;
    if (scrollRect) {
      const visualLeft = rect.left + marginLeftPx;
      const clampedVisualLeft = Math.max(visualLeft, scrollRect.left);
      left = clampedVisualLeft - marginLeftPx;
    }

    setPortalStyle({
      ...style,
      position: "fixed",
      left,
      top: rect.top + rect.height / 2,
    });
  };

  useEffect(() => {
    if (!anchorEl) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  if (!anchorEl || !portalStyle) return null;

  return ReactDOM.createPortal(
    <div className={className} style={portalStyle}>
      {children}
    </div>,
    document.body
  );
};

export type ColumnType =
  | "text"
  | "number"
  | "select"
  | "multiselect"
  | "date"
  | "textbutton"
  | "custom";

export type RowWidgetType = "add" | "delete";

export interface TextCellProps {
  className?: string;
  textClassName?:string;
  textareaClassName?: string;
  containerClassName?: string;
  numberOfRows?: number;
  enableTooltip?: boolean; 
  truncateChars?: number;
}

export interface DateCellProps {
  className?: string;
  inputClassName?: string;
}

export interface NumberCellProps {
  className?: string;
  inputClassName?: string;
}

export interface SelectCellProps {
  placeholder?: string;
  className?: string;
  rootContainerClassName?: string;
  labelClassName?: string;
  showTitleSubTitle?: boolean;
  titleClassName?: string;
  subTitleClassName?: string;
  showTooltip?: boolean;
  footer?: React.ReactNode;
  dropdownClassName?: string;
  allowOpenWhenReadOnly?: boolean;
  hideDisabledOptions?: boolean;
  optionClassName?: string;
}

export interface MultiselectCellProps {
  name: string;
  placeholder?: string;
  className?: string;
  chipClassName?: string;
  rootContainerClassName?: string;
  dropdownClassName?: string;
  renderCustomView?: (selectedOptions: OptionNameValuePair<string>[]) => React.ReactNode;
  showTooltip?: boolean;
}

export interface TextButtonCellProps {
  className?: string;
  buttonClassName?: string;
  variant?:
    | "text"
    | "outline"
    | "primary"
    | "secondary"
    | "ghost"
    | "destructive"
    | "link";
  size?: "sm" | "md" | "lg";
}

export interface IColumnConfig<DataType, FieldType = any> {
  headerName: string;
  isRequired?: boolean;
  headerRender?: ReactNode;
  headerClassName?: string;
  headerStyle?: CSSProperties;
  type: ColumnType;
  field: keyof DataType;
  getValue: (data: DataType) => FieldType;
  hideFocus?: boolean | ((rowData: DataType) => boolean);
  isEditable?:
    | boolean
    | ((
        rowIndex: number,
        rowData: DataType,
        dataRow: IDataRow<DataType>
      ) => boolean);
  onChange?: (
    rowIndex: number,
    value: FieldType,
    field: keyof DataType
  ) => void;
  options?: OptionNameValuePair<FieldType>[];
  render?: (rowIndex: number, value: FieldType) => ReactNode;
  onClick?: (rowIndex: number) => void;
  textCellProps?: TextCellProps;
  dateCellProps?: DateCellProps;
  numberCellProps?: NumberCellProps;
  selectCellProps?: SelectCellProps;
  multiselectCellProps?: MultiselectCellProps;
  textButtonCellProps?: TextButtonCellProps;
  textCellNumberOfRows?: number;
  /** Merged into `<td>` for this column (e.g. width / min-width). */
  tableCellClassName?: string;
  tableCellStyle?: CSSProperties;
}

export interface IDataRow<DataType> {
  data: DataType;
  isEditable?: boolean;
  widgets?: RowWidgetType[];
  onAdd?: (rowIndex: number) => void;
  onDelete?: (rowIndex: number) => void;
}

export interface IBaseTableProps<DataType> {
  columnConfigs: IColumnConfig<DataType>[];
  dataRows: IDataRow<DataType>[];
  selectEnabled?: boolean;
  selectedRows?: number[];
  onRowSelectionsChange?: (rowIndexes: number[]) => void;
  showDefaultTableBody?: boolean;
  defaultTableBody?: React.ReactNode;
  hideTableRowWidget?: boolean;
  checkboxClassName?: string;
  headerCheckboxClassName?: string;
  rounded?: boolean;
  disableFocusWithinStyles?: boolean;
  disableActiveCellStyles?: boolean;
}

const BaseDataTable = <DataType,>(props: IBaseTableProps<DataType>) => {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [activeCell, setActiveCell] = useState<{
    rowIndex: number;
    colIndex: number;
  } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const rowWidgetAnchorRefs = useRef<Map<number, HTMLTableCellElement | null>>(
    new Map()
  );

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target as Node)
      ) {
        setActiveCell(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  const getActiveCellBoxShadow = (args: {
    colIndex: number;
    isLastRow: boolean;
    selectEnabled?: boolean;
  }) => {
    const shadows = [`inset 0 1px 0 0 ${ACTIVE_CELL_BORDER}`];
    if (args.colIndex !== 0 || args.selectEnabled) {
      shadows.push(`inset 1px 0 0 0 ${ACTIVE_CELL_BORDER}`);
    }
    if (args.colIndex !== (props?.columnConfigs?.length ?? 1) - 1) {
      shadows.push(`inset -1px 0 0 0 ${ACTIVE_CELL_BORDER}`);
    }
    if (!args.isLastRow) {
      shadows.push(`inset 0 -1px 0 0 ${ACTIVE_CELL_BORDER}`);
    }
    return shadows.join(", ");
  };

  const onSelectAllChange = (checked: boolean) => {
    if (props?.onRowSelectionsChange) {
      if (checked) {
        props?.onRowSelectionsChange(
          (props?.dataRows || [])?.keys()?.toArray()
        );
      } else {
        props?.onRowSelectionsChange([]);
      }
    }
  };

  const onSelectChange = (rowIndex: number, checked: boolean) => {
    if (props?.onRowSelectionsChange) {
      if (checked) {
        const newSelections = [...(props?.selectedRows || []), rowIndex];
        props?.onRowSelectionsChange(newSelections);
      } else {
        const newSelections = (props?.selectedRows || []).filter(
          (index) => index !== rowIndex
        );
        props?.onRowSelectionsChange(newSelections);
      }
    }
  };

  const renderRowWidgets = (
    rowIndex: number,
    dataRow: IDataRow<DataType>
  ): ReactNode => {
    if (hoveredRowIndex !== rowIndex || !dataRow.widgets?.length) {
      return null;
    }

    const hasDelete = dataRow.widgets.includes("delete");
    const hasAdd = dataRow.widgets.includes("add");

    const anchorEl = rowWidgetAnchorRefs.current.get(rowIndex) ?? null;

    const widgetClassName =
      "absolute left-0 top-1/2 -translate-y-1/2 flex items-center bg-white rounded-lg shadow-md";

    const widgetStyle: CSSProperties = {
      ...(hasDelete && hasAdd ? { width: "50px" } : {}),
      height: "36px",
      borderRadius: "8px",
      padding: "4px",
      gap: "5px",
      ...(hasDelete && hasAdd ? { marginLeft: "-34px" } : { marginLeft: "-14px" }),
      zIndex: 40,
    };

    return (
      <RowWidgetsPortal
        anchorEl={anchorEl}
        className={widgetClassName}
        style={widgetStyle}
      >
        {hasDelete && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              dataRow.onDelete?.(rowIndex);
            }}
            className="flex items-center justify-center hover:opacity-70 transition-opacity flex-shrink-0"
            aria-label="Delete row"
            type="button"
          >
            <Trash2 className="text-[#0A0A0A]" size={17} strokeWidth={2} />
          </button>
        )}
        {hasAdd && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              dataRow.onAdd?.(rowIndex);
            }}
            className="flex items-center justify-center hover:opacity-70 transition-opacity flex-shrink-0"
            aria-label="Add row"
            type="button"
          >
            <Plus className="text-[#3551F3]" size={17} strokeWidth={2} />
          </button>
        )}
      </RowWidgetsPortal>
    );
  };

  const renderCell = (
    columnConfig: IColumnConfig<DataType>,
    rowIndex: number,
    dataRow: IDataRow<DataType>
  ): ReactNode => {
    const value = columnConfig.getValue(dataRow.data);
    const isColumnEditable =
      typeof columnConfig.isEditable === "function"
        ? columnConfig.isEditable(rowIndex, dataRow.data, dataRow)
        : columnConfig.isEditable;

    const isEditable = !!isColumnEditable && !!dataRow.isEditable;
    const onChange = (value: any) => {
      if (columnConfig.onChange) {
        columnConfig.onChange(rowIndex, value, columnConfig.field);
      }
    };

    switch (columnConfig.type) {
      case "text":
        return (
          <TextCell
            value={value}
            isEditable={isEditable}
            onChange={onChange}
            numberOfRows={
              columnConfig?.textCellProps?.numberOfRows ??
              columnConfig?.textCellNumberOfRows
            }
            className={columnConfig?.textCellProps?.className}
            textClassName={columnConfig?.textCellProps?.textClassName} 
            textareaClassName={columnConfig?.textCellProps?.textareaClassName}
            containerClassName={columnConfig?.textCellProps?.containerClassName}
            enableTooltip={columnConfig?.textCellProps?.enableTooltip} 
            truncateChars={columnConfig?.textCellProps?.truncateChars}
          />
        );
      case "select":
        return (
          <SelectCell
            value={value}
            isEditable={isEditable}
            options={columnConfig.options}
            onChange={onChange}
            className={columnConfig?.selectCellProps?.className}
            rootContainerClassName={
              columnConfig?.selectCellProps?.rootContainerClassName
            }
            labelClassName={columnConfig?.selectCellProps?.labelClassName}
            showTitleSubTitle={columnConfig?.selectCellProps?.showTitleSubTitle}
            titleClassName={columnConfig?.selectCellProps?.titleClassName}
            subTitleClassName={columnConfig?.selectCellProps?.subTitleClassName}
            showTooltip={columnConfig?.selectCellProps?.showTooltip}
            footer={columnConfig?.selectCellProps?.footer}
            dropdownClassName={columnConfig?.selectCellProps?.dropdownClassName}
            name={columnConfig?.selectCellProps?.placeholder ?? "Add"}
            allowOpenWhenReadOnly={
              columnConfig?.selectCellProps?.allowOpenWhenReadOnly
            }
            optionClassName={columnConfig?.selectCellProps?.optionClassName}
          />
        );
case "multiselect":
        return (
          <MultiselectCell
            name={columnConfig?.multiselectCellProps?.name}
            placeholder={
              columnConfig?.multiselectCellProps?.placeholder ?? "Add"
            }
            value={value}
            isEditable={isEditable}
            options={columnConfig.options}
            onChange={onChange}
            className={columnConfig?.multiselectCellProps?.className}
            chipClassName={columnConfig?.multiselectCellProps?.chipClassName}
            rootContainerClassName={
              columnConfig?.multiselectCellProps?.rootContainerClassName
            }
            dropdownClassName={
              columnConfig?.multiselectCellProps?.dropdownClassName
            }
            renderCustomView={columnConfig?.multiselectCellProps?.renderCustomView}
            showTooltip={columnConfig?.multiselectCellProps?.showTooltip}
          />
        );
      case "date":
        return (
          <DateCell
            value={value}
            isEditable={isEditable}
            onChange={onChange}
            className={columnConfig?.dateCellProps?.className}
            inputClassName={columnConfig?.dateCellProps?.inputClassName}
          />
        );
      case "number":
        return (
          <NumberCell
            value={value}
            isEditable={isEditable}
            onChange={onChange}
            className={columnConfig?.numberCellProps?.className}
            inputClassName={columnConfig?.numberCellProps?.inputClassName}
          />
        );
      case "textbutton":
        return (
          <TextButtonCell
            value={value}
            onClick={
              columnConfig.onClick
                ? () => columnConfig.onClick!(rowIndex)
                : undefined
            }
            className={columnConfig?.textButtonCellProps?.className}
            buttonClassName={columnConfig?.textButtonCellProps?.buttonClassName}
            variant={columnConfig?.textButtonCellProps?.variant}
            size={columnConfig?.textButtonCellProps?.size}
          />
        );
      case "custom":
        return columnConfig.render ? (
          columnConfig.render(rowIndex, value)
        ) : (
          <></>
        );
      default:
        return <></>;
    }
  };

  const renderHeaderCheckbox = () => (
    <input
      type="checkbox"
      checked={props?.selectedRows?.length === (props?.dataRows || [])?.length}
      onChange={(event) => {
        onSelectAllChange(event.target.checked);
      }}
      className={cn(CHECKBOX_BASE_CLASSES, props?.headerCheckboxClassName)}
    />
  );

  const renderRowCheckbox = (index: number) => (
    <input
      type="checkbox"
      checked={(props.selectedRows || [])?.includes(index)}
      onChange={(event) => {
        onSelectChange(index, event.target.checked);
      }}
      className={cn(CHECKBOX_BASE_CLASSES, props?.checkboxClassName)}
    />
  );

  return (
    <div className="w-full">
      <div
        ref={tableContainerRef}
        className={cn(
          "relative border-[1px] border-[#E5E5E5] overflow-visible min-w-fit",
          props.rounded === false ? "" : "rounded-md",
          props.disableFocusWithinStyles ? "" : "focus-within:border-[#C0C9FB] focus-within:border-[3px]"
        )}
      >
        {(() => {
          const showWidgetCol =
            !props.hideTableRowWidget && !props.selectEnabled;
          const allowRounded = props.rounded !== false;

          return (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {showWidgetCol && (
                    <th
                      className={cn(
                        "bg-[#F1F5F9] p-0 w-0 border-b border-[#E5E5E5]",
                        allowRounded ? "rounded-tl-md" : ""
                      )}
                    />
                  )}
                  {props?.selectEnabled && (
                    <th className={cn("bg-[#F1F5F9] p-2", allowRounded ? "rounded-tl-md" : "")}>
                      <div className="w-8 h-8 flex items-center justify-center">
                        {renderHeaderCheckbox()}
                      </div>
                    </th>
                  )}
                  {props?.columnConfigs.map((columnConfig, colIndex) => {
                    const isFirst = colIndex === 0;
                    const isLast = colIndex === props.columnConfigs?.length - 1;
                    const headerContent =
                      columnConfig.headerRender ?? (
                        <>
                          {columnConfig.headerName}
                          {columnConfig.isRequired && <span className="text-[#DC2626]">*</span>}
                        </>
                      );

                    return (
                      <th
                        key={colIndex}
                        className={cn(
                          `bg-[#F1F5F9] p-2 text-left font-sans font-semibold text-xs leading-[1.125rem] text-[#374151] whitespace-nowrap ${
                          isFirst && !props?.selectEnabled && !showWidgetCol
                            ? allowRounded
                              ? "rounded-tl-md"
                              : ""
                            : ""
                        } ${isLast && allowRounded ? "rounded-tr-md" : ""}`,
                          columnConfig.headerClassName
                        )}
                        style={columnConfig.headerStyle}
                      >
                        {headerContent}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {props.showDefaultTableBody ? (
                  <tr>
                    <td
                      colSpan={
                        props.columnConfigs?.length +
                        (props.selectEnabled ? 1 : 0) +
                        (showWidgetCol ? 1 : 0)
                      }
                      className="p-0"
                    >
                      {props.defaultTableBody}
                    </td>
                  </tr>
                ) : (
                  (props?.dataRows || [])?.map((dataRow, index) => {
                    const rowKey =
                      (dataRow.data as any)?.id ??
                      (dataRow.data as any)?.key ??
                      index;
                    const isLastRow =
                      index === (props?.dataRows || [])?.length - 1;

                    return (
                      <tr
                        key={rowKey}
                        className={cn(
                          isLastRow ? "" : "border-b border-[#E5E5E5]",
                          (props.selectedRows || [])?.includes(index)
                            ? "bg-[#EBEEFE80]"
                            : ""
                        )}
                        onMouseEnter={() => setHoveredRowIndex(index)}
                        onMouseLeave={() => setHoveredRowIndex(null)}
                      >
                        {showWidgetCol && (
                          <td
                            className="relative w-0 p-0"
                            ref={(el) => {
                              rowWidgetAnchorRefs.current.set(index, el);
                            }}
                          >
                            {renderRowWidgets(index, dataRow)}
                          </td>
                        )}
                        {props?.selectEnabled && (
                          <td className="p-2 w-8 h-14 relative">
                            {!props.hideTableRowWidget &&
                              renderRowWidgets(index, dataRow)}
                            <div className="w-8 h-14 flex items-center justify-center gap-2">
                              {renderRowCheckbox(index)}
                            </div>
                          </td>
                        )}
                        {props?.columnConfigs?.map((columnConfig, colIndex) => {
                          const isDecorated =
                            columnConfig.type !== "textbutton";

                            const isFocusHidden =
                          typeof columnConfig.hideFocus === "function"
                            ? columnConfig.hideFocus(dataRow.data)
                            : !!columnConfig.hideFocus;
  
                          const isActiveCell =
                            activeCell?.rowIndex === index &&
                            activeCell?.colIndex === colIndex;

                          const activeCellStyle: CSSProperties | undefined =
                              !props.disableActiveCellStyles &&
                              !isFocusHidden &&
                              isDecorated &&
                              isActiveCell
                              ? {
                                  backgroundColor: ACTIVE_CELL_BG,
                                  boxShadow: getActiveCellBoxShadow({
                                    colIndex,
                                    isLastRow,
                                    selectEnabled: props.selectEnabled,
                                  }),
                                }
                              : undefined;

                          const mergedTdStyle: CSSProperties = {
                            ...columnConfig.tableCellStyle,
                            ...activeCellStyle,
                          };

                          return (
                            <td
                              key={colIndex}
                              className={cn(
                                "p-2 relative",
                                columnConfig.tableCellClassName,
                              )}
                              style={
                                Object.keys(mergedTdStyle).length ? mergedTdStyle : undefined
                              }
                              onMouseDownCapture={() =>{
                                if(!props.disableActiveCellStyles && !isFocusHidden){
                                setActiveCell({ rowIndex: index, colIndex })
                                }
                              }
                              }
                              onFocusCapture={() =>{
                                if(!props.disableActiveCellStyles && !isFocusHidden){
                                setActiveCell({ rowIndex: index, colIndex })
                                }
                              }
                              }
                            >
                              {renderCell(columnConfig, index, dataRow)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
  );
};

export default BaseDataTable;
